import { useThree } from '@react-three/fiber';
import { useMemo } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { ITransformControlsMode } from '../../../../typings';
import { onRemoveSelection, onAddEntityIdsToSelection, onSetPositioningIsActive, onSetRotationIsActive, onSetMultipleComponentProps_Cn_Doc, onSetPositions, onSetTransformControlsMode, onSetMultipleEntitySelectBoundary, onSetActiveAnimationPreset, } from '../../../store/actions';
import { hotkeyIsPressed, IHotkeyTypes, maths, useGetEntityTransientColors, useGetSiblings } from '../../../utils';
import { IComponentType } from '../r3f-components/component-data-structure';
import { getInitialEntityTransformValuesByIds } from '../r3f-components/utils/actions';
import { isAbstractComponent, isCurveComponent } from '../r3f-components/utils/general';
import { getEntityRenderOrder, isHighestPointerPriority } from './hocUtils';
import { REFERENCE_CANVAS_HEIGHT } from '../ScreenRelativeCanvas/ScreenRelativeContent/AnchorGroups/AnchorGroups';
const { vec3 } = maths;
export const useHocPointerOver = (entity, onOver, onOut) => {
    const { id } = entity;
    const siblings = useGetSiblings();
    const renderOrder = getEntityRenderOrder(entity);
    const is3dMode = useSelector((state) => state.userReducer.is3dMode);
    const isScreenRelativeMode = useSelector((state) => state.userReducer.isScreenRelativeMode);
    return (e) => {
        if (!isHighestPointerPriority(id, e.intersections, siblings, renderOrder, is3dMode, isScreenRelativeMode)) {
            onOut?.();
            return;
        }
        onOver();
    };
};
export const useHocEntityPointerMove = (entity, onOver, onOut) => {
    const { id } = entity;
    const siblings = useGetSiblings();
    const renderOrder = getEntityRenderOrder(entity);
    const is3dMode = useSelector((state) => state.userReducer.is3dMode);
    const isScreenRelativeMode = useSelector((state) => state.userReducer.isScreenRelativeMode);
    return (e) => {
        if (e.intersections.length && !isHighestPointerPriority(id, e.intersections, siblings, renderOrder, is3dMode, isScreenRelativeMode)) {
            onOut();
            return;
        }
        onOver();
    };
};
export const useHocPointerOut = (entity, cb) => {
    // const { id } = entity;
    // const siblings = useGetSiblings();
    // const renderOrder = getEntityRenderOrder(entity);
    // const is3dMode = useSelector((state: IDesignerState) => state.userReducer.is3dMode);
    // const isScreenRelativeMode = useSelector((state: IDesignerState) => state.userReducer.isScreenRelativeMode);
    return (e) => {
        // if (intersections.length && !isHighestPointerPriority(id, intersections, siblings, renderOrder, is3dMode, isScreenRelativeMode)) {
        //     console.log('early return', isCurveComponent(entity));
        //     return;
        // }
        cb();
    };
};
export const useShowHighlight = (id, isHovered) => {
    const selectedEntityIds = useSelector((state) => state.userReducer.selectedEntityIds);
    const isDraggingActive = !!useSelector((state) => state.userReducer.entityDragHotspotPosition);
    const is3dMode = useSelector((state) => state.userReducer.is3dMode);
    const isPresetAnimationActive = useSelector((state) => state.userReducer.isPresetAnimationActive);
    const isScreenRelativeMode = useSelector((state) => state.userReducer.isScreenRelativeMode);
    const componentsById = useSelector((state) => state.contentReducer.contentDoc.componentsById);
    const entity = componentsById[id];
    const isEntitySelected = selectedEntityIds?.includes(id);
    // const isAnotherEntitySelected = selectedEntityIds?.length > 0 && !isEntitySelected;
    const isCurved = (entity.type === IComponentType.Image || entity.type === IComponentType.Video || entity.type === IComponentType.Text) && !!entity.curvature;
    return !isCurved && !isPresetAnimationActive && !isDraggingActive && is3dMode && !isScreenRelativeMode && (isEntitySelected || isHovered);
};
export const useHocPointerDown = (args) => {
    const { pointerStageRef, initialPosRef, entity, setShowEntityPositionHotspot } = args;
    const { id } = entity;
    const renderOrder = getEntityRenderOrder(entity);
    const { raycaster, scene, size } = useThree();
    const dispatch = useDispatch();
    const isScreenRelativeMode = useSelector((state) => state.userReducer.isScreenRelativeMode);
    const transformControlsMode = useSelector((state) => state.userReducer.transformControlsMode);
    const hotKeys = useSelector((state) => state.userReducer.hotKeys);
    const selectedEntityIds = useSelector((state) => state.userReducer.selectedEntityIds || []);
    const rotationMarkerPressed = useSelector((state) => state.userReducer.markerIndexPressed === 999);
    const is3dMode = useSelector((state) => state.userReducer.is3dMode);
    const stopPropagation = useIsAllHotspotsCompetingWithSelectedEntityPositionHotspotInactive();
    const screenRelativeScaleType = useSelector((state) => (state.contentReducer.contentDoc.screenRelativeScaleType)) ?? 'height-relative';
    const normalizationFactor = screenRelativeScaleType === 'constant' ? REFERENCE_CANVAS_HEIGHT / size.height : 1;
    const siblings = useGetSiblings();
    return (e) => {
        pointerStageRef.current = 'down';
        if (rotationMarkerPressed || e.buttons === 2)
            return;
        const rayintersects = raycaster.intersectObjects(scene.children).filter(int => int.object.name !== 'emitterAreaLine');
        if (!isHighestPointerPriority(id, rayintersects, siblings, renderOrder, is3dMode, isScreenRelativeMode))
            return;
        // Track pointer on canvas in case user drags over non-canvas (dom) elements
        e.target.setPointerCapture(e.pointerId);
        if (stopPropagation)
            e.stopPropagation();
        const groupSelectHotkeyIsPressedIn2dMode = hotkeyIsPressed(hotKeys, [IHotkeyTypes.groupSelect]) && (!is3dMode || isScreenRelativeMode);
        const entityIsCurrentlySelected = selectedEntityIds.includes(id);
        // In 2D mode, remove OTHER entities from selection ( if there are some group selected ), if user isn't pressing shift
        if (!groupSelectHotkeyIsPressedIn2dMode && !entityIsCurrentlySelected) {
            const otherSelectedEntityIds = selectedEntityIds.filter((selId) => selId !== id);
            dispatch(onRemoveSelection(otherSelectedEntityIds));
        }
        // If shift is pressed and user clicks entity, then the entity should be removed from selection
        // if (groupSelectHotkeyIsPressedIn2dMode && entityIsCurrentlySelected) {
        // 	dispatch(onRemoveSelection([id]));
        // }
        // SR mode doesn't allow group select - remove all selections
        if (!entityIsCurrentlySelected && isScreenRelativeMode)
            dispatch(onRemoveSelection());
        // Add self to selection
        if (!entityIsCurrentlySelected)
            dispatch(onAddEntityIdsToSelection([id]));
        // If a curved entity and transform mode isn't scale, set to curved controls if it's currently snapped to target
        if (isCurveComponent(entity) && transformControlsMode !== ITransformControlsMode.scale && !!entity.isSnappedToTarget) {
            dispatch(onSetTransformControlsMode(ITransformControlsMode.curve));
        }
        // If a curved entity and transform mode is curve, but this entity isn't snapped, reset to Translate
        if (transformControlsMode === ITransformControlsMode.curve && (!isCurveComponent(entity) || !entity.isSnappedToTarget)) {
            dispatch(onSetTransformControlsMode(ITransformControlsMode.translate));
        }
        if (!is3dMode || isScreenRelativeMode)
            dispatch(onSetPositioningIsActive(true));
        dispatch(onSetRotationIsActive(false));
        initialPosRef.current = [e.point.x / normalizationFactor, e.point.y / normalizationFactor, e.point.z / normalizationFactor];
        setShowEntityPositionHotspot?.(true);
        dispatch(onSetActiveAnimationPreset(false));
    };
};
export const useHocPointerMove = (args) => {
    const { pointerStageRef, previousPositionRef, initialPosRef, entity, pointerUpHasRunRef, shiftLockedRef } = args;
    const isLocked = !!entity.isLocked;
    const dispatch = useDispatch();
    const { size } = useThree();
    const is3dMode = useSelector((state) => state.userReducer.is3dMode);
    const isScreenRelativeMode = useSelector((state) => state.userReducer.isScreenRelativeMode);
    const selectedEntityIds = useSelector((state) => state.userReducer.selectedEntityIds || []);
    const componentsById = useSelector((state) => state.contentReducer.contentDoc.componentsById);
    const scaleHotspotIsEnabled = useSelector((state) => state.userReducer.scaleHotspotIsEnabled);
    const selectedContentPositions = useGetSelectedContentPositions();
    const hotkeys = useSelector((state) => state.userReducer.hotKeys);
    const screenRelativeScaleType = useSelector((state) => (state.contentReducer.contentDoc.screenRelativeScaleType)) ?? 'height-relative';
    const normalizationFactor = screenRelativeScaleType === 'constant' ? REFERENCE_CANVAS_HEIGHT / size.height : 1;
    return (e) => {
        // console.log('pointer move 1')
        // console.log('pointer move > pointerStageRef.current: ', pointerStageRef.current);
        if (pointerStageRef.current !== 'down' && pointerStageRef.current !== 'move')
            return;
        pointerStageRef.current = 'move';
        if (pointerUpHasRunRef)
            pointerUpHasRunRef.current = false;
        if (is3dMode && !isScreenRelativeMode)
            return;
        if (scaleHotspotIsEnabled)
            return;
        // Return if entity is locked or entity not dragged
        if (e.buttons === 2 || !selectedEntityIds)
            return;
        if (selectedEntityIds.length === 1 && isLocked)
            return;
        if (selectedEntityIds.length > 1 && selectedEntityIds.filter((id) => componentsById[id].isLocked).length !== 0)
            return;
        // if (doubleClicked) setDoubleClicked(false);
        // Return if position has not changed (re-render and/or continuous firing)
        let { x, y, z } = e.point;
        x = x / normalizationFactor;
        y = y / normalizationFactor;
        z = z / normalizationFactor;
        const noMove = previousPositionRef.current && vec3.equal([x, y, z], previousPositionRef.current);
        if (noMove)
            return;
        previousPositionRef.current = [x, y, z];
        initialPosRef.current = initialPosRef.current || [x, y, z];
        // Calculate pointer offset on hotspot
        const pointerDiff = vec3.subtract([x, y, z], initialPosRef.current);
        if (hotkeys.includes('Shift') && shiftLockedRef?.current == null) {
            shiftLockedRef.current = Math.abs(pointerDiff[0]) > Math.abs(pointerDiff[1]) ? 1 : 0;
        }
        if (typeof shiftLockedRef !== 'undefined' && shiftLockedRef?.current !== null) {
            pointerDiff[shiftLockedRef?.current] = 0;
        }
        if (typeof shiftLockedRef !== 'undefined' && shiftLockedRef.current !== null && !hotkeys.includes('Shift')) {
            shiftLockedRef.current = null;
        }
        const adjTransientPositionDict = {};
        for (let i = 0; i < selectedEntityIds.length; i++) {
            const id = selectedEntityIds[i];
            adjTransientPositionDict[id] = [selectedContentPositions[id][0] + pointerDiff[0], selectedContentPositions[id][1] + pointerDiff[1], selectedContentPositions[id][2]];
        }
        dispatch(onSetPositions(adjTransientPositionDict));
        // console.log('pointer move 2')
    };
};
export const useHocPointerUp = (args) => {
    const { entity, pointerStageRef, initialPosRef, setShowEntityPositionHotspot, pointerUpHasRunRef, shiftLockedRef } = args;
    const { id } = entity;
    const dispatch = useDispatch();
    const is3dMode = useSelector((state) => state.userReducer.is3dMode);
    const isScreenRelativeMode = useSelector((state) => state.userReducer.isScreenRelativeMode);
    const selectedEntityIds = useSelector((state) => state.userReducer.selectedEntityIds || []);
    const selectedTransientPositions = useGetSelectedTransientPositions(id);
    return (e) => {
        // console.log('pointer up 1')
        // console.log('pointer up > pointerStageRef.current: ', pointerStageRef.current);
        // Up can only follow move or down
        if (pointerStageRef.current !== 'move' && pointerStageRef.current !== 'down')
            return;
        dispatch(onSetPositioningIsActive(false));
        if (shiftLockedRef)
            shiftLockedRef.current = null;
        pointerStageRef.current = 'up';
        // console.log('pointer up > pointerUpHasRunRef?.current: ', pointerUpHasRunRef?.current)
        if (pointerUpHasRunRef && pointerUpHasRunRef.current) {
            e.target.releasePointerCapture(e.pointerId);
            e.stopPropagation();
            pointerStageRef.current = null;
            return; // If this is true then it's a double-pointer-up without a pointer move inbetween
        }
        if (e.buttons !== 2 && !!selectedEntityIds && (!is3dMode || isScreenRelativeMode)) {
            const multipleEntityPropsArray_Cn_Doc = [];
            for (let i = 0; i < selectedEntityIds.length; i++) {
                const id = selectedEntityIds[i];
                const entityPos = selectedTransientPositions[id];
                if (entityPos) {
                    multipleEntityPropsArray_Cn_Doc.push({
                        id,
                        position: [entityPos[0], entityPos[1], 0],
                    });
                }
            }
            dispatch(onSetMultipleComponentProps_Cn_Doc(multipleEntityPropsArray_Cn_Doc));
            dispatch(onSetPositions(null));
            dispatch(onSetMultipleEntitySelectBoundary(null));
            if (pointerUpHasRunRef)
                pointerUpHasRunRef.current = true;
        }
        initialPosRef.current = null;
        pointerStageRef.current = null;
        setShowEntityPositionHotspot?.(false);
        // console.log('pointer up 2')
    };
};
export const useGetSelectedContentPositions = () => {
    const contentPositions = useGetContentPositions();
    const selectedEntityIds = useSelector((state) => state.userReducer.selectedEntityIds);
    return useMemo(() => {
        const selectedContentPositions = {};
        if (!selectedEntityIds?.length)
            return selectedContentPositions;
        for (let i = 0; i < selectedEntityIds.length; i++) {
            const contentPosition = contentPositions[selectedEntityIds[i]];
            if (!contentPosition)
                continue;
            selectedContentPositions[selectedEntityIds[i]] = [...contentPosition];
        }
        return selectedContentPositions;
    }, [selectedEntityIds, contentPositions]);
};
export const useGetSelectedTransientPositions = (id) => {
    const transientPosition = useSelector((state) => state.userReducer.positionById?.[id] || null);
    const transientPositionDict = useSelector((state) => state.userReducer.positionById);
    const selectedEntityIds = useSelector((state) => state.userReducer.selectedEntityIds);
    return useMemo(() => {
        const selectedPositionsPositions = {};
        if (!selectedEntityIds?.length)
            return selectedPositionsPositions;
        for (let i = 0; i < selectedEntityIds.length; i++) {
            if (transientPositionDict[selectedEntityIds[i]]) {
                if (transientPositionDict[selectedEntityIds[i]] !== null)
                    selectedPositionsPositions[selectedEntityIds[i]] = [...transientPositionDict[selectedEntityIds[i]]];
            }
            else {
                selectedPositionsPositions[selectedEntityIds[i]] = null;
            }
        }
        return selectedPositionsPositions;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedEntityIds, transientPosition]);
};
export const useGetContentPositions = () => {
    const componentsById = useSelector((state) => state.contentReducer.contentDoc.componentsById);
    return useMemo(() => {
        const cnPositions = {};
        Object.keys(componentsById).forEach((id) => {
            const component = componentsById[id];
            if (!!(!isAbstractComponent(component) && component?.type)) {
                cnPositions[id] = [...component.position];
            }
        });
        return cnPositions;
    }, [componentsById]);
};
export const useIsAllHotspotsCompetingWithSelectedEntityPositionHotspotInactive = () => {
    const scaleHotspotIsEnabled = useSelector((state) => state.userReducer.scaleHotspotIsEnabled);
    const rotationIsActive = useSelector((state) => state.userReducer.rotationIsActive);
    const backgroundHotspotIsEnabled = useSelector((state) => state.userReducer.backgroundHotspotIsEnabled);
    return !scaleHotspotIsEnabled && !rotationIsActive && !backgroundHotspotIsEnabled;
};
export const useGetHocCurvedProperties = (entity) => {
    const curvature = useSelector((state) => {
        return state.userReducer?.curvatureById?.[entity.id] || entity.curvature;
    });
    return { curvature };
};
export const useGetHocSpatialProperties = (entity) => {
    const [sx, sy, sz] = useSelector((state) => {
        return state.userReducer.scaleById[entity.id] || entity.scale;
    }, shallowEqual);
    const [rx, ry, rz] = useSelector((state) => {
        return state.userReducer.rotationById[entity.id] || entity.rotation;
    }, shallowEqual);
    const position = useSelector((state) => {
        return state.userReducer.positionById[entity.id] || entity.position;
    }, shallowEqual);
    const rotation = [maths.toRadians(rx), maths.toRadians(ry), maths.toRadians(rz)];
    const scale = [sx * 2, sy * 2, sz * 2];
    return {
        scale,
        position,
        rotation,
    };
};
export const useGetMultipleComponentSpatialProperties = (ids) => {
    const componentsById = useSelector((state) => state.contentReducer.contentDoc.componentsById);
    const transientScalesById = useSelector((state) => state.userReducer.scaleById);
    const transientRotationsById = useSelector((state) => state.userReducer.rotationById);
    const transientPositionsById = useSelector((state) => state.userReducer.positionById);
    return getInitialEntityTransformValuesByIds({ ids, componentsById, transientScalesById, transientRotationsById, transientPositionsById });
};
export const useGetHocTextProperties = (entity) => {
    const { transientFontRgba, transientFillRgba } = useGetEntityTransientColors(entity.id);
    const fontRgba = transientFontRgba || entity.fontRgba || [0, 0, 0, 1];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const color = transientFillRgba || entity.color;
    return { fontRgba, color };
};
export const useGetHocBorderProperties = (entity) => {
    const { transientBorderRgba } = useGetEntityTransientColors(entity.id);
    const borderWidth = entity.borderWidth || 0;
    const borderRadius = entity.borderRadius || 0;
    const borderRgba = transientBorderRgba || entity.borderRgba || [0, 0, 0, 1];
    return { borderWidth, borderRadius, borderRgba };
};
